import {
  isCancelled,
  isExternal,
  MAIL_SHIFT_STATES,
  mailboxKey,
  mailboxSiblings,
  mailboxUnits,
  type Lang,
} from '@reditelji/shared';
import type { PostRow } from '../api/posts';
import type { EventDay, Shift } from '../api/types';
import type { VolunteerRow } from '../api/volunteers';
import { rollupFor, schedStateFor, type SchedState } from '../volunteers/derive';
import { liveFilter, matchesAll } from '../volunteers/tagLogic';

/**
 * Who an obvestilo can be written to, and which of them a coordinator's
 * filters leave on screen (SPEC §1.1H).
 *
 * Pure, so the rules a coordinator relies on when they tick "everyone in Bled
 * on Saturday" are pinned by tests rather than by reading a component.
 */

/** The confirmation states a notice can be aimed at — the Prostovoljci pill's own kinds. */
export const NOTICE_STATES = ['potrjen', 'caka', 'changed', 'zavrnjen', 'none'] as const;
export type NoticeState = (typeof NOTICE_STATES)[number];

export interface NoticeFilters {
  query: string;
  states: NoticeState[];
  dayIds: string[];
  sectorIds: string[];
  postIds: string[];
  shifts: 'vsi' | 'z' | 'brez';
  lang: 'vsi' | Lang;
  tagIds: string[];
}

export const EMPTY_FILTERS: NoticeFilters = {
  query: '',
  states: [],
  dayIds: [],
  sectorIds: [],
  postIds: [],
  shifts: 'vsi',
  lang: 'vsi',
  tagIds: [],
};

/** Where one of a person's shifts is — what the day, sector and post filters ask of it. */
export interface ShiftFacet {
  dayId: string;
  sectorId: string;
  postId: string;
}

export interface NoticeCandidate {
  v: VolunteerRow;
  state: SchedState;
  /** Their shifts the mail rules count: assigned or done, at a post that still exists. */
  shifts: ShiftFacet[];
  /** Why they cannot be ticked, or null. */
  unavailable: string | null;
}

const countsForMail = (state: string) => (MAIL_SHIFT_STATES as readonly string[]).includes(state);

/**
 * Everyone an obvestilo could be aimed at, by name.
 *
 * External teams never reach the roster payload; somebody who is not coming is
 * listed only while the coordinator has chosen to include them. A person with
 * no address is listed — the coordinator is looking for them — but cannot be
 * ticked.
 */
export function noticeCandidates(input: {
  volunteers: readonly VolunteerRow[];
  shifts: readonly Shift[];
  posts: readonly PostRow[];
  days: readonly EventDay[];
  includeCancelled: boolean;
}): NoticeCandidate[] {
  const postById = new Map(input.posts.map((p) => [p.id, p]));
  const byVolunteer = new Map<string, Shift[]>();
  for (const s of input.shifts) {
    if (s.volunteerId === null) continue;
    byVolunteer.set(s.volunteerId, [...(byVolunteer.get(s.volunteerId) ?? []), s]);
  }
  return input.volunteers
    .filter((v) => v.active && !isExternal(v))
    .filter((v) => input.includeCancelled || !isCancelled(v))
    .map((v) => {
      const own = byVolunteer.get(v.id) ?? [];
      const rollup = rollupFor(v, own, [...input.days], postById);
      const shifts = own
        .filter((s) => countsForMail(s.state))
        .flatMap((s) => {
          const p = postById.get(s.postId);
          return p ? [{ dayId: s.eventDayId, sectorId: p.sectorId, postId: p.id }] : [];
        });
      return {
        v,
        state: schedStateFor(v, rollup),
        shifts,
        unavailable: v.email ? null : 'brez e-naslova',
      };
    })
    .sort((a, b) => a.v.name.localeCompare(b.v.name, 'sl'));
}

/**
 * The filters without ids that no longer name anything — a deleted tag, post,
 * sector or day — so a draft saved last week cannot hide the whole roster and
 * take the selection with it.
 */
export function liveNoticeFilters(
  f: NoticeFilters,
  known: {
    tagIds: ReadonlyMap<string, unknown>;
    postIds: ReadonlyMap<string, unknown>;
    sectorIds: ReadonlyMap<string, unknown>;
    dayIds: ReadonlyMap<string, unknown>;
  },
): NoticeFilters {
  return {
    ...f,
    tagIds: liveFilter(f.tagIds, known.tagIds),
    postIds: liveFilter(f.postIds, known.postIds),
    sectorIds: liveFilter(f.sectorIds, known.sectorIds),
    dayIds: liveFilter(f.dayIds, known.dayIds),
  };
}

/**
 * Whether the filters leave this person on screen.
 *
 * Every group narrows (AND); within one group any choice will do (OR), except
 * tags, which read as on Prostovoljci — every tag picked. Day, sector and post
 * are asked of one shift together: "Bled on Saturday" is somebody with a
 * Saturday shift in Bled, not somebody in Bled on Sunday who also works a
 * Saturday elsewhere.
 */
export function matchesNotice(c: NoticeCandidate, f: NoticeFilters): boolean {
  const q = f.query.trim().toLocaleLowerCase('sl');
  if (q && !c.v.name.toLocaleLowerCase('sl').includes(q)) return false;
  if (f.states.length > 0 && !(f.states as readonly string[]).includes(c.state.kind)) return false;
  if (f.lang !== 'vsi' && c.v.lang !== f.lang) return false;
  if (!matchesAll(c.v.tagIds, f.tagIds)) return false;
  if (f.shifts === 'z' && c.shifts.length === 0) return false;
  if (f.shifts === 'brez' && c.shifts.length > 0) return false;
  const bySlot = f.dayIds.length > 0 || f.sectorIds.length > 0 || f.postIds.length > 0;
  if (!bySlot) return true;
  return c.shifts.some(
    (s) =>
      (f.dayIds.length === 0 || f.dayIds.includes(s.dayId)) &&
      (f.sectorIds.length === 0 || f.sectorIds.includes(s.sectorId)) &&
      (f.postIds.length === 0 || f.postIds.includes(s.postId)),
  );
}

/**
 * The selection once the screen has changed: whoever the filters hide is
 * unticked, as on Prejemniki, so a send never reaches somebody the coordinator
 * can no longer see.
 *
 * One exception: a housemate added from the confirmation stays while somebody
 * they share the address with is still ticked and shown — they were added
 * *because* of that person, and the filters that hid them were never about
 * them. They go when that person goes, or when they can no longer be written
 * to at all.
 *
 * Returns the same array when nothing changes, so a caller can compare.
 */
export function prunePicked(input: {
  picked: readonly string[];
  addedHousemates: readonly string[];
  visible: readonly NoticeCandidate[];
  candidates: readonly NoticeCandidate[];
}): readonly string[] {
  const shown = new Set(input.visible.filter((c) => c.unavailable === null).map((c) => c.v.id));
  const byId = new Map(input.candidates.map((c) => [c.v.id, c]));
  const shownKeys = new Set(
    input.picked
      .filter((id) => shown.has(id))
      .map((id) => byId.get(id))
      .map((c) => (c ? mailboxKey(c.v) : null))
      .filter((k) => k !== null),
  );
  const next = input.picked.filter((id) => {
    if (shown.has(id)) return true;
    if (!input.addedHousemates.includes(id)) return false;
    const c = byId.get(id);
    if (!c || c.unavailable !== null) return false;
    const key = mailboxKey(c.v);
    return key !== null && shownKeys.has(key);
  });
  return next.length === input.picked.length ? input.picked : next;
}

/** A housemate of somebody ticked who is not ticked themselves. */
export interface UnpickedHousemate {
  person: VolunteerRow;
  /** The ticked person they share the address with — the first by name. */
  withPerson: VolunteerRow;
  /** Reads the other language, so they would get a mail of their own. */
  otherLang: boolean;
}

/**
 * Who reads a ticked person's inbox but is not ticked — the people a coordinator
 * may want to add before a notice goes out (SPEC §1.1E). Only people who could
 * receive it: listed as candidates, with an address.
 */
export function unpickedHousemates(
  picked: readonly string[],
  candidates: readonly NoticeCandidate[],
): UnpickedHousemate[] {
  const pickedSet = new Set(picked);
  const roster = candidates.map((c) => c.v);
  const receivable = new Set(candidates.filter((c) => c.unavailable === null).map((c) => c.v.id));
  const found = new Map<string, UnpickedHousemate>();
  for (const c of candidates) {
    if (!pickedSet.has(c.v.id)) continue;
    for (const sibling of mailboxSiblings(c.v, roster)) {
      if (pickedSet.has(sibling.id) || !receivable.has(sibling.id) || found.has(sibling.id)) continue;
      found.set(sibling.id, { person: sibling, withPerson: c.v, otherLang: sibling.lang !== c.v.lang });
    }
  }
  return [...found.values()].sort((a, b) => a.person.name.localeCompare(b.person.name, 'sl'));
}

/** The languages the ticked people read — the ones the notice has to be written in. */
export function langsOf(picked: readonly string[], candidates: readonly NoticeCandidate[]): Lang[] {
  const pickedSet = new Set(picked);
  return [...new Set(candidates.filter((c) => pickedSet.has(c.v.id)).map((c) => c.v.lang))];
}

/**
 * Who shares this ticked person's mail: the other ticked people on their
 * address who read their language — the same unit the batch will send one mail
 * to, and what their preview is rendered with.
 */
export function unitMates(
  id: string,
  picked: readonly string[],
  candidates: readonly NoticeCandidate[],
): string[] {
  const pickedSet = new Set(picked);
  const people = candidates.filter((c) => pickedSet.has(c.v.id)).map((c) => c.v);
  const units = mailboxUnits(people);
  const unit = units.get(id);
  if (unit === undefined) return [];
  return people.filter((v) => v.id !== id && units.get(v.id) === unit).map((v) => v.id);
}
